import React, { useState } from 'react';
import { Table, Button, Space, Tag, Tooltip, message, Switch } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  BankOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import BankModal from '../../../../components/modelpopup/BankModal';
import {
  useGetBanksQuery,
  useSaveBankMutation,
} from '../../../../services/settings.service';
import { BANKS } from '../../../../services/ENDPOINTS';
import { useDispatch } from 'react-redux';
import { toggleDelete } from '../../../../redux/slices/ui.slice';

const Banks = () => {
  const [modalVisible, setModalVisible] = useState(false);
  const [editingBank, setEditingBank] = useState(null);
  const dispatch = useDispatch();

  // API Hooks
  const { data: banksData, isLoading, refetch } = useGetBanksQuery();
  const [saveBank] = useSaveBankMutation();

  const banks = banksData?.data || [];

  // Handle Add
  const handleAdd = () => {
    setEditingBank(null);
    setModalVisible(true);
  };

  // Handle Edit
  const handleEdit = (bank) => {
    setEditingBank(bank);
    setModalVisible(true);
  };

  // Handle Delete
  const doDelete = (e, item) => {
    dispatch(
      toggleDelete({
        open: true,
        title: 'Bank',
        url: `${BANKS}/${item.id}`,
        ele: e.currentTarget,
        parent: 'tr'
      })
    );
  };

  // Handle Active Toggle
  const handleActiveToggle = async (record) => {
    try {
      await saveBank({
        ...record,
        isActive: !record.isActive,
      }).unwrap();
      message.success(`Bank ${record.isActive ? 'deactivated' : 'activated'} successfully`);
      refetch();
    } catch (error) {
      message.error('Failed to update status');
    }
  };

  // Table Columns
  const columns = [
    {
      title: 'Bank Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>
            <BankOutlined style={{ marginRight: 8, color: '#1890ff' }} />
            {text}
          </div>
          {record.branch && (
            <div style={{ fontSize: '12px', color: '#888', marginTop: 4 }}>
              Branch: {record.branch}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Bank Code',
      dataIndex: 'code',
      key: 'code',
      width: 120,
      render: (code) => code ? <Tag color="blue">{code}</Tag> : <span style={{ color: '#888' }}>-</span>,
    },
    {
      title: 'SWIFT Code',
      dataIndex: 'swiftCode',
      key: 'swiftCode',
      width: 150,
      render: (swift) => swift || <span style={{ color: '#888' }}>-</span>,
    },
    {
      title: 'IFSC Code',
      dataIndex: 'ifscCode',
      key: 'ifscCode',
      width: 150,
      render: (ifsc) => ifsc || <span style={{ color: '#888' }}>-</span>,
    },
    {
      title: 'Active',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (isActive, record) => (
        <Switch
          checked={isActive}
          onChange={() => handleActiveToggle(record)}
          checkedChildren="Active"
          unCheckedChildren="Inactive"
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={(e) => doDelete(e, record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div className="page-wrapper">
        <div className="content container-fluid">
          {/* Breadcrumb */}
          <div className="page-header">
            <div className="row align-items-center">
              <div className="col">
                <h3 className="page-title">Banks</h3>
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/employee-dashboard">Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item active">Settings</li>
                </ul>
              </div>
              <div className="col-auto float-end ms-auto">
                <Link
                  to="#"
                  className="btn add-btn"
                  onClick={handleAdd}
                >
                  <i className="fa fa-plus" />
                  Add Bank
                </Link>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-12">
              <div className="card mb-0">
                <div className="card-header">
                  <div className="row align-items-center">
                    <div className="col">
                      <h4 className="card-title mb-0">Banks</h4>
                      <p className="text-muted mb-0">
                        Manage bank details for payroll and salary processing
                      </p>
                    </div>
                  </div>
                </div>

                <div className="card-body">
                  <Table
                    columns={columns}
                    dataSource={banks}
                    rowKey="id"
                    loading={isLoading}
                    scroll={{ x: 900 }}
                    pagination={{
                      pageSize: 10,
                      showSizeChanger: true,
                      showTotal: (total) => `Total ${total} banks`,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <BankModal
        visible={modalVisible}
        bank={editingBank}
        onClose={() => {
          setModalVisible(false);
          setEditingBank(null);
        }}
        onSuccess={() => {
          setModalVisible(false);
          setEditingBank(null);
          refetch();
        }}
      />
    </>
  );
};

export default Banks;
